import type { CSSProperties, ReactNode } from 'react'
import { TAP_MIN, radius, theme } from '../theme'

type Tone = 'primary' | 'child' | 'quiet' | 'danger' | 'ghost'

const tones: Record<Tone, CSSProperties> = {
  primary: { background: theme.blue, color: '#FFFFFF', border: 'none' },
  child: { background: theme.orange, color: '#FFFFFF', border: 'none' },
  quiet: { background: '#FFFFFF', color: theme.blue, border: `1.5px solid ${theme.blueTint}` },
  danger: { background: '#FFFFFF', color: theme.red, border: `1.5px solid ${theme.red}` },
  ghost: { background: 'transparent', color: theme.muted, border: `1.5px solid ${theme.border}` },
}

export function Button({
  children, onClick, tone = 'primary', disabled, style, type = 'button', full,
}: {
  children: ReactNode
  onClick?: () => void
  tone?: Tone
  disabled?: boolean
  style?: CSSProperties
  type?: 'button' | 'submit'
  full?: boolean
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        font: `700 16px ${'inherit'}`,
        fontFamily: 'inherit',
        fontSize: 16,
        fontWeight: 700,
        borderRadius: radius.md,
        padding: '12px 24px',
        minHeight: TAP_MIN,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        width: full ? '100%' : undefined,
        ...tones[tone],
        ...style,
      }}
    >
      {children}
    </button>
  )
}

export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: theme.card,
        borderRadius: radius.lg,
        border: `1px solid ${theme.borderWarm}`,
        // 24 on a laptop, 16 on a phone. Fixed 24 cost 48px of a 360px
        // screen, which is what pushed the spelling keyboard off the edge.
        padding: 'clamp(16px, 4vw, 24px)',
        boxShadow: '0 1px 2px rgba(21,32,43,0.04)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export function Page({
  children, width = 560, style,
}: { children: ReactNode; width?: number; style?: CSSProperties }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        background: theme.bg,
        // --ws-top is set by ExitButton while it is on screen, so the
        // pinned Home button never sits on top of a heading.
        paddingTop: 'max(var(--ws-top, 20px), env(safe-area-inset-top, 0px))',
        paddingBottom: 'max(24px, env(safe-area-inset-bottom, 0px))',
        paddingLeft: 16,
        paddingRight: 16,
        boxSizing: 'border-box',
        ...style,
      }}
    >
      <div style={{ maxWidth: width, margin: '0 auto' }}>{children}</div>
    </div>
  )
}

export function Heading({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: theme.text, lineHeight: 1.2 }}>
        {children}
      </h1>
      {sub ? (
        <p style={{ margin: '8px 0 0', fontSize: 16, color: theme.muted, lineHeight: 1.5 }}>
          {sub}
        </p>
      ) : null}
    </div>
  )
}

export function Field({
  label, hint, children,
}: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 18 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.text2, marginBottom: 6 }}>
        {label}
      </div>
      {hint ? (
        <div style={{ fontSize: 14, color: theme.faint, marginBottom: 8, lineHeight: 1.45 }}>
          {hint}
        </div>
      ) : null}
      {children}
    </label>
  )
}

/**
 * The shared look for a text box.
 *
 * A getter, not a plain object. As a constant it read `theme.text` and
 * `theme.border` once when this module was first imported, which froze it to
 * whichever palette happened to be active then — so every input in the app
 * kept the default colours no matter what the child chose. A getter re-reads
 * the live palette each time it is used.
 *
 * Anything else that wants to build a style from theme values must do it
 * INSIDE a component, for the same reason.
 */
const INPUT_BASE: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  fontSize: 17,
  borderRadius: radius.md,
  padding: '11px 13px',
  minHeight: TAP_MIN,
}

export const styles = {
  get input(): CSSProperties {
    return {
      ...INPUT_BASE,
      color: theme.text,
      background: theme.card,
      border: `2px solid ${theme.border}`,
    }
  },
}

/**
 * Kept as a named export because half the screens already import it.
 *
 * ALWAYS spread it: style={{ ...inputStyle }}, never style={inputStyle}.
 * It is a Proxy (so it follows the colour theme), and React freezes the
 * style object it is given; freezing this proxy throws, and the whole
 * screen goes blank. That is what happened to Settings on 8 Oct 2026, right
 * after Sam typed the new grown-up PIN.
 */
export const inputStyle = new Proxy({} as CSSProperties, {
  get: (_t, key: string) => (styles.input as Record<string, unknown>)[key],
  ownKeys: () => Reflect.ownKeys(styles.input),
  getOwnPropertyDescriptor: (_t, key) => ({
    ...Object.getOwnPropertyDescriptor(styles.input, key), configurable: true,
  }),
  has: (_t, key) => key in styles.input,
}) as CSSProperties

/** A row of choices where exactly one is picked. Used for the year offset. */
export function ChoiceRow<T extends string | number>({
  options, value, onChange, labels,
}: {
  options: readonly T[]
  value: T
  onChange: (v: T) => void
  labels?: (v: T) => string
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((option) => {
        const on = option === value
        return (
          <button
            key={String(option)}
            type="button"
            onClick={() => onChange(option)}
            style={{
              fontFamily: 'inherit',
              fontSize: 15,
              fontWeight: on ? 700 : 600,
              color: on ? theme.text : theme.text2,
              background: on ? theme.orangePale : '#FFFFFF',
              border: on ? `2.5px solid ${theme.orange}` : `2px solid ${theme.border}`,
              borderRadius: radius.sm,
              padding: '9px 13px',
              minHeight: TAP_MIN,
              cursor: 'pointer',
            }}
          >
            {labels ? labels(option) : String(option)}
          </button>
        )
      })}
    </div>
  )
}

export function Progress({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  return (
    <div
      role="progressbar"
      aria-valuenow={done}
      aria-valuemax={total}
      style={{ height: 8, background: theme.orangeWarm, borderRadius: radius.pill, overflow: 'hidden' }}
    >
      <div style={{ width: `${pct}%`, height: '100%', background: theme.orange, transition: 'width 220ms' }} />
    </div>
  )
}
